import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Paper, Avatar, Button, CircularProgress, TextField, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase/firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
<link href="https://fonts.googleapis.com/css2?family=Newsreader:wght@400;700&display=swap" rel="stylesheet"></link>

const Profile = () => {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState({});
  const [updateLoading, setUpdateLoading] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        if (currentUser?.uid) {
          const userDoc = await getDoc(doc(db, "users", currentUser.uid));
          if (userDoc.exists()) {
            setProfile(userDoc.data());
          }
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [currentUser]);

  useEffect(() => {
    if (profile) {
      setEditedProfile({
        name: profile.name || '',
        phone: profile.phone || '',
      });
    }
  }, [profile]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (profile) {
      setEditedProfile({
        name: profile.name || '',
        phone: profile.phone || '',
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!currentUser?.uid) return;
    
    setUpdateLoading(true);
    try {
      await updateDoc(doc(db, "users", currentUser.uid), {
        ...editedProfile,
        updatedAt: serverTimestamp()
      });
      
      setProfile(prev => ({
        ...prev,
        ...editedProfile,
        updatedAt: new Date()
      }));
      
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
    } finally {
      setUpdateLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ backgroundColor: '#B2C770', height: '20vh', width: '100%' }} />
      <Container maxWidth="lg" sx={{ mt: -15, mb: 4, position: 'relative', zIndex: 1 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 0 }}>
          <Box display="flex" flexDirection="row" justifyContent="space-between" mb={4}>
            <Box>
              <Typography variant="h6" mb={5} textAlign={'left'} color='#597D3B'>
                User Profile
              </Typography>
              <Typography variant="h4" fontWeight="bold" fontFamily={'Newsreader'}>
                {profile?.name || 'User'}
              </Typography>
            </Box>
            <Avatar 
              sx={{ 
                width: 120, 
                height: 120, 
                bgcolor: '#2e7d32',
                fontSize: '3rem',
                ml: 4
              }}
            >
              {profile?.name?.charAt(0)?.toUpperCase() || currentUser?.email?.charAt(0)?.toUpperCase()}
            </Avatar>
          </Box>
          
          <Box 
            display="flex"
            gap={4}
            mt={4}
            sx={{flexDirection: { xs: 'column', md: 'row' }}}
          >
            <Box flex={3}>
              <Typography variant="h5" mb={5} fontFamily={'inter'}>
                Recent Uploads
              </Typography>
              {[1, 2].map((item, i) => (
                <Paper key={i} sx={{ p: 4, mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fff9ed'}}>
                  <Avatar variant="rounded" src={`/uploads/img${item}.png`} sx={{ width: 64, height: 64, mr: 2 }} />
                  <Box flex={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" fontFamily={'inter'}>Image {item}</Typography>
                      <Typography variant="caption" fontFamily={'inter'} sx={{ ml: 2 }}>
                        {item === 1 ? '01/04/2025' : '23/05/2025'}
                      </Typography>
                    </Box>
                    <Button variant="outlined" sx={{ mt: 1, display:"flex", justifyContent:"space-between", alignItems:"baseline", borderRadius: 5}}>View</Button>
                  </Box>
                </Paper>
              ))}
              <Box display="flex" justifyContent="center" mt={4}>
                <Button 
                  variant="contained" 
                  sx={{ 
                    bgcolor: '#2e7d32',
                    '&:hover': { bgcolor: '#1b5e20' }
                  }}
                >
                  View All Uploads
                </Button>
              </Box>
            </Box>
            
            <Box flex={2}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: '#fff9ed',
                  borderRadius: 3,
                  p: 3,
                }}
              >
                <Typography variant="h6" fontWeight="bold" gutterBottom textAlign={'left'} fontFamily={'inter'}>
                  Profile Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2, mt: 2 }} >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1" fontFamily={'inter'} fontWeight="medium">Email</Typography>
                    <Typography variant="body1" fontFamily={'inter'}>{currentUser?.email}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1" fontFamily={'inter'} fontWeight="medium">Name</Typography>
                    <Typography variant="body1" fontFamily={'inter'}>{profile?.name || 'Not provided'}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1" fontFamily={'inter'} fontWeight="medium">Phone</Typography>
                    <Typography variant="body1" fontFamily={'inter'}>{profile?.phone || 'Not provided'}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1" fontFamily={'inter'} fontWeight="medium">Member Since</Typography>
                    <Typography variant="body1" fontFamily={'inter'}>
                      {profile?.createdAt?.toDate().toLocaleDateString() || 'Unknown'}
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" justifyContent="center" mt={4}>
                  <Button 
                    variant="contained" 
                    onClick={handleEditClick}
                    sx={{ 
                      bgcolor: '#2e7d32',
                      '&:hover': { bgcolor: '#1b5e20' }
                    }}
                  >
                    Edit Profile
                  </Button>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Paper>
      </Container>

      <Dialog 
        open={isEditing} 
        onClose={handleCancel}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ bgcolor: '#2e7d32', color: 'white', fontFamily: 'Newsreader' }}>
          Edit Profile
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, my: 2 }}>
            <TextField
              label="Name"
              name="name"
              value={editedProfile.name || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Phone"
              name="phone"
              value={editedProfile.phone || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Email"
              value={currentUser?.email || ''}
              disabled
              fullWidth
              variant="outlined"
              helperText="Email cannot be changed here. Please contact support."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCancel} 
            color="inherit"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={updateLoading}
            sx={{ 
              bgcolor: '#2e7d32',
              '&:hover': { bgcolor: '#1b5e20' }
            }}
          >
            {updateLoading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Profile;